import { Link } from 'react-router-dom'
import AppFooter from '../components/shared/AppFooter'

export default function PrivacyPolicyPage() {
  return (
    <div className="relative flex min-h-screen flex-col bg-base text-ink">
      <div className="mx-auto max-w-3xl px-4 py-16">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-ink/60 hover:text-ink">
          ← Back
        </Link>

        <h1 className="font-display text-3xl font-bold text-ink">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-amber/60">Last updated: March 18, 2026</p>

        <div className="mt-8 space-y-10">
          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">1. Introduction</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              Omore (“the Service”) is operated by OMore (“we”, “us”, “our”). We are committed
              to protecting your privacy, especially the privacy of young users. This policy explains
              what data we collect, how we use it, and your rights.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              For a plain-language overview of our safety approach, visit our{' '}
              <Link to="/safety" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                Safety
              </Link>{' '}
              page.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">2. How the Service Works — and What That Means for Data</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              The Service has two modes of use:
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              <strong>Guest use (no account):</strong> You can create and send up to 5 postys per day without
              providing any personal information. We automatically create a temporary anonymous session to enable
              this — it contains no identifying information and is deleted automatically after 7 days of inactivity.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              <strong>Registered use:</strong> To access landscape building, classroom features, and your posty
              history, you can create an account. Account creation requires only a username and password. We do not
              collect your email address, real name, phone number, or any other personal identifiers.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">3. Data We Collect</h2>
            <div className="overflow-x-auto rounded-xl border border-amber/20">
              <table className="w-full min-w-[400px] text-sm">
                <thead className="bg-amber/10">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-ink">Data</th>
                    <th className="px-4 py-3 text-left font-semibold text-ink">Purpose</th>
                    <th className="px-4 py-3 text-left font-semibold text-ink">Where Stored</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  <tr>
                    <td className="px-4 py-3 text-ink">Username</td>
                    <td className="px-4 py-3 text-ink/70">Your chosen account identifier (3–20 lowercase letters/numbers)</td>
                    <td className="px-4 py-3 text-ink/70">Firebase Auth + Firestore</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-ink">Password</td>
                    <td className="px-4 py-3 text-ink/70">Account security (hashed by Firebase; we never see it in plain text)</td>
                    <td className="px-4 py-3 text-ink/70">Firebase Auth</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-ink">Anonymous session ID</td>
                    <td className="px-4 py-3 text-ink/70">Enables guest posty creation without sign-up</td>
                    <td className="px-4 py-3 text-ink/70">Firebase Auth (auto-deleted after 7 days)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-ink">Posty content</td>
                    <td className="px-4 py-3 text-ink/70">Recipient name, sender name, chosen message, mood, and visual selections</td>
                    <td className="px-4 py-3 text-ink/70">Firestore + Cloud Storage</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-ink">Landscape data</td>
                    <td className="px-4 py-3 text-ink/70">Which template you have chosen and which elements you have earned</td>
                    <td className="px-4 py-3 text-ink/70">Firestore</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-ink">Classroom membership</td>
                    <td className="px-4 py-3 text-ink/70">Linking your account to a shared classroom landscape</td>
                    <td className="px-4 py-3 text-ink/70">Firestore</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-ink">Usage counters</td>
                    <td className="px-4 py-3 text-ink/70">Number of postys sent, streak, milestone level</td>
                    <td className="px-4 py-3 text-ink/70">Firestore</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm leading-relaxed text-ink/70">
              <strong>A note on names in postys:</strong> The recipient and sender names you enter when creating
              a posty (for example, “for Priya, from Arjun”) are limited to 20 letters, are checked against a
              profanity filter before submission, are not linked to your account, and are not stored as part of
              your profile.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">4. Data We Do Not Collect</h2>
            <div className="rounded-xl bg-surface/50 p-4">
              <ul className="ml-4 list-disc space-y-1 text-sm text-ink/70">
                <li>Email address or phone number</li>
                <li>Real name, home address, or date of birth</li>
                <li>Location data or GPS coordinates</li>
                <li>Photos from your camera roll or gallery</li>
                <li>Contacts or address book</li>
                <li>Browsing history outside of Omore</li>
                <li>Financial or payment information</li>
                <li>Device fingerprinting data</li>
              </ul>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">5. How We Use Your Data</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              We use your data solely to:
            </p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-ink/70">
              <li>Provide and operate the Service (creating postys, building landscapes, classroom features)</li>
              <li>Authenticate your account securely</li>
              <li>Moderate content to maintain a safe environment</li>
              <li>Improve the Service based on aggregate, anonymised usage patterns</li>
            </ul>
            <p className="text-sm leading-relaxed text-ink/70">
              We do <strong>not</strong> sell, rent, or share your personal data with third parties for
              advertising or marketing purposes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">6. Third-Party Services</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              We use Google Firebase to power the Service. Firebase provides authentication, database,
              file storage, and hosting. Google processes data in accordance with their{' '}
              <a
                href="https://firebase.google.com/support/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber"
              >
                Firebase Privacy Policy
              </a>.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              We use Firebase Analytics to understand aggregate usage patterns (such as which features are used
              most). This does not involve tracking individual users or building advertising profiles.
            </p>
            <p className="text-sm leading-relaxed text-ink/70">
              We do not use advertising SDKs, social media tracking pixels, or third-party marketing tools.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">7. Children’s Privacy</h2>
            <div className="rounded-xl bg-surface/50 p-4">
              <p className="text-sm leading-relaxed text-ink/70">
                Omore is designed with young users in mind. We take extra care to protect children’s privacy:
              </p>
              <ul className="ml-4 mt-2 list-disc space-y-1 text-sm text-ink/70">
                <li>We do not collect email addresses, real names, or any contact information</li>
                <li>There is no direct messaging, public profiles, or social networking between users</li>
                <li>All user-generated content is moderated</li>
                <li>We do not serve targeted advertising to any users</li>
                <li>No account is required to use the core features of the Service</li>
              </ul>
            </div>
            <p className="text-sm leading-relaxed text-ink/70">
              Because we do not collect personal identifiers, we are unable to verify the age of our users.
              We encourage parents and guardians of users under 13 to review these terms. If you believe a
              child has created an account and you would like it removed, contact us at{' '}
              <a href="mailto:go@omore.app" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                go@omore.app
              </a>{' '}
              and we will promptly delete it.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">8. Data Retention</h2>
            <ul className="ml-4 list-disc space-y-1 text-sm text-ink/70">
              <li><strong>Anonymous sessions</strong> are automatically deleted after 7 days of inactivity</li>
              <li><strong>Posty share links</strong> expire automatically after 7 days</li>
              <li><strong>Registered accounts</strong> retain data for as long as the account is active</li>
              <li>
                <strong>Account deletion</strong> can be initiated directly within the app. This permanently
                deletes your profile, landscape, and associated data. We process deletion requests within 30 days.
                Anonymised aggregate data (such as total postys sent counters) may be retained as it cannot be
                linked to you
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">9. Your Rights</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              Depending on your location, you may have the right to:
            </p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-ink/70">
              <li><strong>Access</strong> the data we hold about you</li>
              <li><strong>Correct</strong> inaccurate data</li>
              <li><strong>Delete</strong> your account and associated data (available directly in-app, or by emailing us)</li>
              <li><strong>Object</strong> to certain processing of your data</li>
            </ul>
            <p className="text-sm leading-relaxed text-ink/70">
              To exercise any of these rights, email us at{' '}
              <a href="mailto:go@omore.app" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                go@omore.app
              </a>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">10. Cookies and Local Storage</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              We use essential session cookies only — specifically, Firebase authentication session cookies
              required to keep you signed in. We do not use tracking cookies, advertising cookies, or persistent
              cross-site identifiers.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">11. Security</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              We use industry-standard security measures provided by Google Firebase, including encrypted
              data transmission (HTTPS/TLS), server-enforced access controls, and hashed password storage.
              While no system is completely secure, we take reasonable steps to protect your data.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">12. Changes to This Policy</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              We may update this Privacy Policy from time to time. If we make material changes, we will
              notify you through the Service. The “Last updated” date at the top reflects when the policy
              was last revised.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-display text-xl font-bold text-ink">13. Contact</h2>
            <p className="text-sm leading-relaxed text-ink/70">
              For privacy-related questions, data requests, or concerns, contact us at{' '}
              <a href="mailto:go@omore.app" className="font-medium text-amber underline decoration-amber/30 hover:decoration-amber">
                go@omore.app
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex h-12 items-center rounded-2xl bg-amber px-6 font-marker text-base text-base shadow-glow-amber transition-all hover:brightness-105 active:scale-[0.97]"
          >
            Back to Home
          </Link>
        </div>
      </div>
      <AppFooter />
    </div>
  )
}
